// Cliente API del módulo financiero (011a + 011b + 011c).
// Reutiliza el wrapper request() de libs/http/http vía common.js para mantener autenticación jwt_token.

import { request } from "../libs/http/http";
import axios from "axios";
import { getToken } from "../helpers/helpers";

// =====================================================
// Transacciones pendientes (entregas + traslados al usuario actual)
// =====================================================
export const obtenerTransaccionesPendientes = () =>
  request('get', `/finanzas/transacciones-pendientes`);

// =====================================================
// Bolsillo (vista compuesta)
// =====================================================
export const getBolsilloMio = () => request('get', `/Bolsillo/mio`);
// Items "actuales" del bolsillo: excluye los ya cerrados en relaciones
// APROBADA/SINCRONIZADA+. Los del borrador vienen en `data.enBorrador.*`.
export const getBolsilloActuales = () => request('get', `/Bolsillo/mio/actuales`);
export const getBolsilloDeUsuario = (id) => request('get', `/Bolsillo/usuario/${id}`);

// =====================================================
// Entregas de dinero
// =====================================================
export const crearEntregaDinero = (data) => request('post', `/EntregaDinero/crear`, data);
export const listarEntregas = (filtros = {}) => request('post', `/EntregaDinero/listar`, filtros);
export const obtenerEntrega = (id) => request('get', `/EntregaDinero/obtener/${id}`);
export const entregasPendientesMias = () => request('get', `/EntregaDinero/pendientes/mias`);
export const confirmarEntrega = (id, datos = {}) => request('post', `/EntregaDinero/confirmar/${id}`, datos);
export const rechazarEntrega = (id, motivo) => request('post', `/EntregaDinero/rechazar/${id}`, { motivo });
export const anularEntrega = (id, justificacion) => request('post', `/EntregaDinero/anular/${id}`, { justificacion });

// =====================================================
// Traslados (rutas Ops de 011b)
// =====================================================
export const crearTraslado = (data) => request('post', `/TrasladoDineroOps/crear`, data);
export const listarTraslados = (filtros = {}) => request('post', `/TrasladoDineroOps/listar`, filtros);
export const obtenerTrasladoOps = (id) => request('get', `/TrasladoDineroOps/obtener/${id}`);
export const trasladosPendientesMios = () => request('get', `/TrasladoDineroOps/pendientes/mios`);
export const confirmarTraslado = (id, datos = {}) => request('post', `/TrasladoDineroOps/confirmar/${id}`, datos);
export const rechazarTraslado = (id, motivo) => request('post', `/TrasladoDineroOps/rechazar/${id}`, { motivo });
export const anularTraslado = (id, justificacion) => request('post', `/TrasladoDineroOps/anular/${id}`, { justificacion });

// =====================================================
// Gastos (rutas Ops de 011b)
// =====================================================
export const registrarGasto = (data) => request('post', `/GastoContableOps/registrar`, data);
export const listarGastos = (filtros = {}) => request('post', `/GastoContableOps/listar`, filtros);
export const obtenerGasto = (id) => request('get', `/GastoContableOps/obtener/${id}`);
export const editarGasto = (id, data) => request('put', `/GastoContableOps/editar/${id}`, data);
export const anularGasto = (id, justificacion) => request('post', `/GastoContableOps/anular/${id}`, { justificacion });

// =====================================================
// Movimientos del bolsillo (ledger)
// =====================================================
export const movimientosDelBolsillo = (bolsilloId, params = {}) =>
  request('get', `/MovimientoBolsillo/bolsillo/${bolsilloId}${buildQuery(params)}`);

// =====================================================
// Relaciones de gastos (cierre)
// =====================================================
export const listarRelaciones = (filtros = {}) => request('post', `/RelacionGastos/listar`, filtros);
export const obtenerRelacion = (id) => request('get', `/RelacionGastos/obtener/${id}`);
export const iniciarRelacion = (bolsilloId) => request('post', `/RelacionGastos/iniciar`, { bolsilloId });
export const aprobarRelacion = (id, observaciones) => request('post', `/RelacionGastos/${id}/aprobar`, { observaciones });
export const cancelarRelacion = (id) => request('post', `/RelacionGastos/${id}/cancelar`);
export const previewRelacion = (id) => request('get', `/RelacionGastos/${id}/preview`);
export const verificarRelacion = (id) => request('post', `/RelacionGastos/${id}/verificar`);

// Solo cambia el tercero de un gasto (proveedor o captura manual).
export const cambiarTerceroGasto = (gastoId, payload) =>
  request('put', `/GastoContableOps/cambiarTercero/${gastoId}`, payload);
/**
 * Descarga PDF de una RelacionGastos. Devuelve un blob.
 * Usamos axios directo porque el wrapper request no soporta responseType: 'blob'.
 */
export const descargarPdfRelacion = (id) => {
  axios.defaults.headers.common.jwt_token = getToken();
  return axios({
    method: 'get',
    url: `/RelacionGastos/${id}/pdf`,
    responseType: 'blob'
  });
};

// =====================================================
// Comprobantes contables (administración de sync)
// =====================================================
export const listarComprobantes = (params = {}) =>
  request('get', `/ComprobanteContableOps${buildQuery(params)}`);
export const historialSyncComprobante = (id) =>
  request('get', `/ComprobanteContableOps/${id}/historial-sync`);
export const forzarSyncComprobante = (id) =>
  request('post', `/ComprobanteContableOps/${id}/forzar-sync`);
export const marcarSincronizadoManual = (id, externalId, justificacion) =>
  request('post', `/ComprobanteContableOps/${id}/marcar-sincronizado-manual`, { externalId, justificacion });
export const flushNoConfigurados = () =>
  request('post', `/ComprobanteContableOps/flush-no-configurados`);

// =====================================================
// Configuración contable (singleton)
// =====================================================
export const obtenerConfiguracionContable = () =>
  request('get', `/ConfiguracionContable/singleton`);
export const actualizarConfiguracionContable = (data) =>
  request('put', `/ConfiguracionContable/singleton`, data);

// =====================================================
// Proveedores (buscador + crear inline)
// =====================================================
export const buscarProveedores = (texto, limit = 10) => {
  const t = (texto || '').trim();
  if (t.length < 2) return Promise.resolve({ data: { data: [] } });
  const regex = { $regex: t, $options: 'i' };
  return request('post', `/proveedores/listar`, {
    conditions: {
      $or: [{ nombre: regex }, { nit: regex }],
      estado: 'Activo'
    },
    options: { limit, sort: { nombre: 1 } }
  });
};

export const crearProveedorRapido = (data) =>
  request('post', `/proveedores/crear`, data);

// =====================================================
// Cuentas contables (filtros para selectores)
// =====================================================
export const cuentasFuenteFondos = () =>
  request('post', `/cuentasContables/listar`, { conditions: { esFuenteFondos: true, activo: true } });
export const cuentasBolsillosPersonales = () =>
  request('post', `/cuentasContables/listar`, { conditions: { esBolsilloPersonal: true, activo: true } });
export const cuentasDeGastos = () =>
  request('post', `/cuentasContables/listar`, { conditions: { clase: { $in: ['5', '6'] }, activo: true } });

// Lectura de cuentas contables desde el proveedor externo (Alegra/Siigo).
// El backend orquesta provider -> endpoint -> flatten -> mapping inbound.
// El cache vive en React Query (ver useCuentasContablesExternas).
export const listarCuentasContablesExternas = () =>
  request('get', `/cuentasContables/desdeProveedor`);

// Lectura de cuentas-banco del proveedor externo. Las cuentas-banco son una
// coleccion separada del PUC: tienen su propio id y se relacionan manualmente
// con una cuenta contable via el campo bankId al crearla.
export const listarCuentasBancoExternas = () =>
  request('get', `/cuentasContables/cuentasBanco/desdeProveedor`);

// =====================================================
// Terceros (proveedores) — local + externo
// =====================================================
export const buscarProveedoresLocal = (texto, limit = 20) => {
  const t = (texto || '').trim();
  const regex = t.length >= 2 ? { $regex: t, $options: 'i' } : null;
  const conditions = regex
    ? { $or: [{ nombre: regex }, { nit: regex }], estado: 'Activo' }
    : { estado: 'Activo' };
  return request('post', `/proveedores/listar`, {
    conditions,
    options: { limit, sort: { nombre: 1 } }
  });
};

// Busca un tercero en el ERP externo por NIT. El backend agrega el query
// param correcto del proveedor (ej: ?identification=XXX en Alegra). Si nit
// es vacio el backend no consulta al ERP, devuelve lista vacia.
export const buscarProveedorExternoPorNit = (nit) =>
  request('get', `/proveedores/desdeProveedor${buildQuery({ nit })}`);

// Crea un Proveedor local a partir de la data ya mapeada del ERP externo.
// Backend es idempotente por idContable + nit.
export const crearProveedorDesdeExterno = (datos) =>
  request('post', `/proveedores/crearDesdeExterno`, datos);

// =====================================================
// Informe Financiero (vista administrativa)
// =====================================================
export const generarInformeFinanciero = (filtros = {}) =>
  request('post', '/InformeFinanciero/generar', filtros);

// =====================================================
// Helpers
// =====================================================
function buildQuery(params) {
  const qs = Object.entries(params)
    .filter(([_, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return qs ? `?${qs}` : '';
}
